import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiUsers } from 'react-icons/fi';
import { useParent } from '../context/ParentContext';
import './StudentSwitcher.css';

function StudentSwitcher() {
  const { students, selectedStudent, selectedStudentId, switchStudent } = useParent();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return '#22c55e';
      case 'needs-attention': return '#f59e0b';
      case 'at-risk': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getInitials = (student) => {
    if (!student) return '?';
    const first = student.firstName?.[0] || '';
    const last = student.lastName?.[0] || '';
    const initials = `${first}${last}`.trim();
    return initials || (student.email?.[0] || '?').toUpperCase();
  };

  if (!selectedStudent) {
    return (
      <div className="student-switcher" ref={dropdownRef}>
        <button
          className="student-switcher__trigger"
          onClick={() => {}}
          disabled
          aria-label="No student selected"
        >
          <div className="student-switcher__avatar">
            <FiUsers size={14} />
          </div>
          <div className="student-switcher__info">
            <span className="student-switcher__name">No student</span>
            <span className="student-switcher__meta">Add a student</span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="student-switcher" ref={dropdownRef}>
      <button
        className="student-switcher__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Selected student: ${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim()}
      >
        <div className="student-switcher__avatar">
          {getInitials(selectedStudent)}
        </div>
        <div className="student-switcher__info">
          <span className="student-switcher__name">
            {selectedStudent.firstName || ''} {selectedStudent.lastName || ''}
          </span>
          <span className="student-switcher__meta">
            {selectedStudent.grade ? `Grade ${selectedStudent.grade}` : (selectedStudent.email || '')}
            <span
              className="student-switcher__status-dot"
              style={{ background: getStatusColor(selectedStudent.overallStatus) }}
              aria-label={selectedStudent.overallStatus?.replace('-', ' ')}
            />
          </span>
        </div>
        <FiChevronDown className={`student-switcher__chevron ${isOpen ? 'student-switcher__chevron--open' : ''}`} />
      </button>

      {isOpen && (
        <div className="student-switcher__dropdown" role="listbox" aria-label="Select student">
          <button
            className={`student-switcher__option ${selectedStudentId === 'all' ? 'student-switcher__option--active' : ''}`}
            onClick={() => { switchStudent('all'); setIsOpen(false); }}
            role="option"
            aria-selected={selectedStudentId === 'all'}
          >
            <div className="student-switcher__option-avatar student-switcher__option-avatar--all">
              <FiUsers size={14} />
            </div>
            <div className="student-switcher__option-info">
              <span className="student-switcher__option-name">All Students</span>
              <span className="student-switcher__option-meta">{students.length} students</span>
            </div>
          </button>

          {students.map(student => (
            <button
              key={student.id}
              className={`student-switcher__option ${selectedStudentId === student.id ? 'student-switcher__option--active' : ''}`}
              onClick={() => { switchStudent(student.id); setIsOpen(false); }}
              role="option"
              aria-selected={selectedStudentId === student.id}
            >
              <div className="student-switcher__option-avatar">
                {getInitials(student)}
              </div>
              <div className="student-switcher__option-info">
                <span className="student-switcher__option-name">
                  {student.firstName} {student.lastName}
                </span>
                <span className="student-switcher__option-meta">
                  Grade {student.grade}
                  <span
                    className="student-switcher__status-dot"
                    style={{ background: getStatusColor(student.overallStatus) }}
                  />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentSwitcher;
